import axios from "axios";

// =============================
// Base setup
// =============================
const API_URL = `${process.env.REACT_APP_BASE_URL}/`;

const axiosApi = axios.create({
  baseURL: API_URL,
  withCredentials: true, // important for cookies (refresh token)
});

// =============================
// Helper: set Authorization header
// =============================
export function setAuthHeader(token) {
  axiosApi.defaults.headers.common["Authorization"] = token ? token : "";
}

// Initial token load
const authUser = localStorage.getItem("authUser");
const token = authUser ? JSON.parse(authUser).token || "" : "";
setAuthHeader(token);

// =============================
// Interceptor for token refresh
// =============================
axiosApi.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If token expired & not retried yet
    if (error.response?.status === 403  && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Call refresh endpoint
        const res = await axios.post(
          `${API_URL}refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = res.data.token;

        // Save updated token in localStorage
        const authUser = JSON.parse(localStorage.getItem("authUser")) || {};
        authUser.token = newToken;
        localStorage.setItem("authUser", JSON.stringify(authUser));
        // Update axios default header
        setAuthHeader(newToken);

        // Retry original request with new token
        originalRequest.headers["Authorization"] = newToken;
        return axiosApi(originalRequest);
      } catch (refreshError) {
        // If refresh fails → logout
        localStorage.removeItem("authUser");
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

// =============================
// API helper functions
// =============================
export async function get(url, config = {}) {
  return axiosApi.get(url, { ...config }).then((response) => response.data);
}

export async function post(url, data, config = {}) {
  return axiosApi
    .post(url, { ...data }, { ...config })
    .then((response) => response.data)   
}

export async function put(url, data, config = {}) {
  return axiosApi
    .put(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function patch(url, data, config = {}) {
  return axiosApi
    .patch(url, { ...data }, { ...config })
    .then((response) => response.data);
}

export async function del(url, config = {}) {
  return axiosApi
    .delete(url, { ...config })
    .then((response) => response.data);
}

export default axiosApi;
